import React, { useMemo } from 'react';
import fallbackImage from '../../images/fallback/plantFallbackImage.png';
import "../../css/dashboardStylings/DashboardPlantStatusStyling.css";

// Memoized individual plant card component
const PlantCard = React.memo(({ plantEntry, plantData, progress, daysLeft, statusColor, progressBackground, onPlantStatusClick }) => {
    // Convert hex color to rgba for shadow
    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const shadowColor = hexToRgba(statusColor, 0.3);
    const borderColor = hexToRgba(statusColor, 0.3);

    return (
        <div className="plant-status-card"
             style={{
                 '--hover-shadow': `0 12px 32px ${shadowColor}`,
                 '--hover-border': borderColor
             }}
        >
            <div
                className="plant-image-container"
                style={{ background: progressBackground }}
            >
                <div className="plant-image-inner">
                    <img
                        src={plantData.optimized_plant_image_url || fallbackImage}
                        alt={plantData.plant_name}
                        className="plant-image"
                        onError={(e) => {
                            e.target.src = fallbackImage || 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=100&h=100&fit=crop'; // CHANGE THIS!! lol
                        }}
                    />
                </div>
                {progress > 0 && (
                    <div
                        className="progress-text"
                        style={{ color: statusColor }}
                    >
                        {Math.round(progress)}%
                    </div>
                )}
            </div>

            <div className="plant-info">
                <h3 className="plant-name">{plantData.plant_name}</h3>
                <p className="plant-type">{plantData.plant_type} — {plantData.crop_type}</p>
                <div className="plant-details">
                    <span>Around <span className="highlight-green">{daysLeft}</span> days to maturity</span>
                </div>
            </div>

            <div className={`status-indicator ${plantEntry.plant_current_stage_name ?? plantEntry.plant_status}`}>
                {plantEntry.plant_current_stage_name ?? plantEntry.plant_status}
            </div>

            {plantEntry.planted_date && (
                <div className="planted-date">
                    Planted: {new Date(plantEntry.planted_date).toLocaleDateString()}
                </div>
            )}

            {!plantEntry.planted_date && (
                <div className="planted-date">
                    Not yet planted
                </div>
            )}
        </div>
    );
});



const DashboardPlantStatusComponent = React.memo(({ allUserPlants, onPlantStatusClick }) => {
    // Memoized utility functions
    const calculateProgress = useMemo(() => (plantedDate, daysToMaturity) => {
        if (!plantedDate || !daysToMaturity) return 0;

        const planted = new Date(plantedDate);
        const today = new Date();
        const daysPassed = Math.floor((today - planted) / (1000 * 60 * 60 * 24));

        return Math.min(100, Math.max(0, (daysPassed / daysToMaturity) * 100));
    }, []);

    const getDaysToMaturity = useMemo(() => (plantedDate, daysToMaturity) => {
        if (!plantedDate || !daysToMaturity) return daysToMaturity;

        const planted = new Date(plantedDate);
        const today = new Date();
        const daysPassed = Math.floor((today - planted) / (1000 * 60 * 60 * 24));

        return Math.max(0, daysToMaturity - daysPassed);
    }, []);

    // Memoized status color mapping
    const statusColors = useMemo(() => ({
        'planned': '#6b7280',
        'planted': '#f59e0b',
        'Germination': '#10b981',
        'Sprouted': '#22c55e',
        'Seedling': '#16a34a',
        'Established': '#3b82f6',
        'Vegetative': '#047857',
        'Growing': '#047857',
        'Budding': '#ec4899',
        'Flowering': '#d946ef',
        'Fruiting': '#ea580c',
        'Podding': '#ea580c',
        'Ripening': '#ef4444',
        'Maturing': '#ef4444',
        'Harvest': '#f59e0b',
        'Post-Harvest': '#9ca3af',
        'Dormant': '#9ca3af',
        'Regrowing': '#9ca3af',
        'Dead': '#dc2626',
        'Dying': '#dc2626',
        'Removed': '#dc2626',
        'Early Vegetative': '#166534',
        'Late Vegetative': '#065f46',
        'Root Maturation': '#92400e',
        'Dormancy': '#4b5563',
        'Reproductive Growth': '#7c3aed',
        'Tuber Initiation': '#9a3412',
        'Tuber Bulking': '#ca8a04',
        'Seed Formation': '#a16207',
        'Root Development': '#7c2d12',
        'Cold Stratification': '#1e40af',
        'Shoot Emergence': '#065f46',
        'Bulb Regeneration': '#6b21a8'
    }), []);

    const getStatusColor = useMemo(() => (status) => {
        return statusColors[status] || '#28a644';
    }, [statusColors]);

    // Memoized display plants selection
    const displayPlants = useMemo(() => {
        if (!allUserPlants || allUserPlants.length === 0) return [];

        // Group plants by status
        const statusGroups = allUserPlants.reduce((acc, plant) => {
            const status = plant.plant_current_stage_name || plant.plant_status || 'planned';
            if (!acc[status]) acc[status] = [];
            acc[status].push(plant);
            return acc;
        }, {});

        const displayPlants = [];
        const statusPriority = [
            'Harvest', 'Ripening', 'Fruiting', 'Flowering', 'Late Vegetative', 'Early Vegetative', 'Vegetative',
            'Dormancy', 'Reproductive Growth', 'Tuber Initiation', 'Tuber Bulking', 'Seed Formation',
            'Root Development', 'Cold Stratification', 'Shoot Emergence', 'Bulb Regeneration', 'Root Maturation',
            'Established', 'Seedling', 'Sprouted', 'Germination', 'planted', 'planned'
        ];

        // Select plants by priority
        statusPriority.forEach(status => {
            if (statusGroups[status] && displayPlants.length < 12) {
                const needed = Math.min(3, 12 - displayPlants.length);
                displayPlants.push(...statusGroups[status].slice(0, needed));
            }
        });

        // Fill remaining slots if needed
        if (displayPlants.length < 12) {
            const remaining = allUserPlants.filter(plant => !displayPlants.includes(plant));
            displayPlants.push(...remaining.slice(0, 12 - displayPlants.length));
        }

        return displayPlants.slice(0, 12);
    }, [allUserPlants]);

    // Memoized processed plant data with all calculations
    const processedPlantData = useMemo(() => {
        return displayPlants.map(plantEntry => {
            const plantData = plantEntry.plant;
            const progress = calculateProgress(plantEntry.planted_date, plantData.days_to_maturity);
            const daysLeft = getDaysToMaturity(plantEntry.planted_date, plantData.days_to_maturity);
            const statusColor = getStatusColor(plantEntry.plant_current_stage_name ?? plantEntry.plant_status);

            const progressBackground = progress > 0
                ? `conic-gradient(
                    from -90deg,
                    ${statusColor} 0deg ${progress * 3.6}deg,
                    #e5e7eb ${progress * 3.6}deg 360deg
                )`
                : '#e5e7eb';

            return {
                plantEntry,
                plantData,
                progress,
                daysLeft,
                statusColor,
                progressBackground
            };
        });
    }, [displayPlants, calculateProgress, getDaysToMaturity, getStatusColor]);

    // Early return for empty state
    if (displayPlants.length === 0) {
        return (
            <div className="empty-entries-body">
                <div className="no-entries-message">
                    <div>No plants to show, be sure to plant your gardens!</div>
                    <button className="start-entry-button" onClick={onPlantStatusClick}>
                        Get Started
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="plant-status-card-body">
            <div className="plant-status-card-grid">
                {processedPlantData.map(({ plantEntry, plantData, progress, daysLeft, statusColor, progressBackground }) => (
                    <PlantCard
                        key={plantEntry.id}
                        plantEntry={plantEntry}
                        plantData={plantData}
                        progress={progress}
                        daysLeft={daysLeft}
                        statusColor={statusColor}
                        progressBackground={progressBackground}
                        onPlantStatusClick={onPlantStatusClick}
                    />
                ))}
            </div>

            <div className="click-to-view-all-button" onClick={onPlantStatusClick}>
                <div className="click-to-view-all-text">View and update your plants</div>
            </div>
        </div>
    );
});

// Add display names for debugging
// DashboardPlantStatusComponent.displayName = 'DashboardPlantStatusComponent';
// PlantCard.displayName = 'PlantCard';

export default DashboardPlantStatusComponent;